import { useState, useRef, useCallback, useEffect } from "react";
import { Table, Tag, Avatar, Tooltip, Typography } from "antd";
import axiosInstance from "../../API/interceptor";
import { priorityCfg, statusCfg } from "./data";
import { ListViewRaciModal } from "./RaciPopover";
import RaciPersonCell from "./PersonCell";
import TaskDetailPanel from "./TaskDetailPanel";
import CommentPanel from "./CommentPanel";
import CategoryEditPopup from "./CategoryEditPopup";

const { Text } = Typography;

/* ══ HELPERS ══════════════════════════════════════════════════ */
const colorMap = ["#6366F1", "#8B5CF6", "#EC4899", "#F59E0B", "#10B981", "#3B82F6"];
const avatarColor = (name = "", idx = 0) =>
  colorMap[((name || "").charCodeAt(0) + idx) % colorMap.length];


const PersonCell = ({ name, idx }) => {
  const nameStr = Array.isArray(name) ? name[0] : name;
  return nameStr ? (
    <div className="flex items-center gap-1.5">
      <Avatar
        size={24}
        style={{ background: avatarColor(nameStr, idx) }}
        className="!text-[10px] !shrink-0"
      >
        {nameStr.slice(0, 2).toUpperCase()}
      </Avatar>
      <Text className="!text-xs">{nameStr}</Text>
    </div>
  ) : (
    <Text type="secondary" className="!text-xs">—</Text>
  );
};

// Convert UTC server time to browser's local timezone for display
// For local-naive stored dates (task_created_at): stored as IST naive, getUTC* reads IST value
const fmtDate = (v) => {
  if (!v) return null;
  try {
    const dt = new Date(v);
    if (isNaN(dt.getTime())) return null;
    const dd      = String(dt.getUTCDate()).padStart(2, "0");
    const mm      = String(dt.getUTCMonth() + 1).padStart(2, "0");
    const hours24 = dt.getUTCHours();
    const hours12 = hours24 % 12 || 12;
    const ampm    = hours24 >= 12 ? "PM" : "AM";
    const hh      = String(hours12).padStart(2, "0");
    const min     = String(dt.getUTCMinutes()).padStart(2, "0");
    return `${dd}-${mm}-${dt.getUTCFullYear()} ${hh}:${min} ${ampm}`;
  } catch { return null; }
};

// For actual-UTC stored dates (start_date, completion_sla, completed_at): convert UTC → local
const fmtDateLocal = (v) => {
  if (!v) return null;
  try {
    const dt = new Date(v);
    if (isNaN(dt.getTime())) return null;
    const dd      = String(dt.getDate()).padStart(2, "0");
    const mm      = String(dt.getMonth() + 1).padStart(2, "0");
    const hours24 = dt.getHours();
    const hours12 = hours24 % 12 || 12;
    const ampm    = hours24 >= 12 ? "PM" : "AM";
    const hh      = String(hours12).padStart(2, "0");
    const min     = String(dt.getMinutes()).padStart(2, "0");
    return `${dd}-${mm}-${dt.getFullYear()} ${hh}:${min} ${ampm}`;
  } catch { return null; }
};

/* ══ PHOTO AVATAR ═════════════════════════════════════════════ */
const photoCache     = {};
const photoListeners = {};

function PhotoAvatar({ name, userId, size = 24, style = {} }) {
  const [src, setSrc] = useState(
    typeof photoCache[userId] === "string" &&
    photoCache[userId] !== "loading" &&
    photoCache[userId] !== "none"
      ? photoCache[userId] : null
  );

  useEffect(() => {
    if (!userId) return;
    if (
      typeof photoCache[userId] === "string" &&
      photoCache[userId] !== "loading" &&
      photoCache[userId] !== "none"
    ) { setSrc(photoCache[userId]); return; }

    if (!photoListeners[userId]) photoListeners[userId] = new Set();
    photoListeners[userId].add(setSrc);
    if (photoCache[userId] === "loading") return;

    photoCache[userId] = "loading";
    axiosInstance.get(`/task_assignment/get_user_photo/${userId}`)
      .then(res => {
        if (res.data?.success && res.data?.photo) {
          photoCache[userId] = res.data.photo;
          photoListeners[userId]?.forEach(fn => fn(res.data.photo));
        } else {
          photoCache[userId] = "none";
        }
      })
      .catch(() => { photoCache[userId] = "none"; });

    return () => { photoListeners[userId]?.delete(setSrc); };
  }, [userId]);

  // NOTE: size and dynamic avatar bg color must stay inline — computed at runtime
  return src && src !== "loading" && src !== "none" ? (
    <img
      src={src}
      alt={name}
      className="rounded-full object-cover shrink-0"
      style={{ width: size, height: size, ...style }}
    />
  ) : (
    <Avatar
      size={size}
      style={{ background: avatarColor(name || "", 0), fontSize: size * 0.38, flexShrink: 0, ...style }}
    >
      {(name || "?").slice(0, 2).toUpperCase()}
    </Avatar>
  );
}

/* ══ DATE CELL ════════════════════════════════════════════════ */
const DateCell = ({ v }) =>
  fmtDate(v)
    ? <span className="text-xs text-gray-700 whitespace-nowrap">{fmtDate(v)}</span>
    : v
      ? <span className="text-xs text-gray-700 whitespace-nowrap">{v}</span>
      : <span className="text-xs text-gray-400">--</span>;

const DateCellLocal = ({ v }) =>
  fmtDateLocal(v)
    ? <span className="text-xs text-gray-700 whitespace-nowrap">{fmtDateLocal(v)}</span>
    : v
      ? <span className="text-xs text-gray-700 whitespace-nowrap">{v}</span>
      : <span className="text-xs text-gray-400">--</span>;

/* ══ COMMENT ICON ═════════════════════════════════════════════ */
const CommentIcon = () => (
  <svg
    width="15" height="15" viewBox="0 0 24 24" fill="none"
    stroke="#2563eb" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
  >
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

/* ══ MORE NAMES PILL ══════════════════════════════════════════
   Shows first `showCount` names, rest behind a "+N more" pill.
   Hover on pill → floating popup with remaining names + avatars.
══════════════════════════════════════════════════════════════ */
function MoreNamesPill({ names = [], ids = [], size = 22, showCount = 1 }) {
  const [open,    setOpen] = useState(false);
  const [rect,    setRect] = useState(null);
  const pillRef            = useRef();
  const hideTimer          = useRef(null);

  const cancelHide   = () => clearTimeout(hideTimer.current);
  const scheduleHide = () => { hideTimer.current = setTimeout(() => setOpen(false), 220); };

  const visible   = names.slice(0, showCount);
  const hidden    = names.slice(showCount);
  const hiddenIds = ids.slice(showCount);

  if (names.length === 0) return <Text type="secondary" className="!text-xs">—</Text>;

  return (
    <div className="flex flex-col gap-[5px] w-full">
      {/* Names before the last visible one */}
      {visible.slice(0, -1).map((name, ni) => (
        <RaciPersonCell key={ni} name={name} personId={ids[ni]} idx={ni} size={size} />
      ))}

      {/* Last visible name — inline with +N pill pinned to the right */}
      {visible.length > 0 && (
        <div className="flex items-center gap-[4px] flex-nowrap w-full">
          <div className="flex-1 min-w-0 overflow-hidden">
            <RaciPersonCell
              name={visible[visible.length - 1]}
              personId={ids[visible.length - 1]}
              idx={visible.length - 1}
              size={size}
            />
          </div>
          {hidden.length > 0 && (
            <>
              <div
                ref={pillRef}
                onMouseEnter={() => {
                  cancelHide();
                  if (pillRef.current) setRect(pillRef.current.getBoundingClientRect());
                  setOpen(true);
                }}
                onMouseLeave={scheduleHide}
                className={`inline-flex items-center justify-center w-[22px] h-[22px] rounded-full
                  text-[10px] font-bold cursor-pointer select-none transition-all duration-150 border shrink-0
                  ${open
                    ? "bg-indigo-500 text-white border-indigo-500"
                    : "bg-indigo-50 text-indigo-600 border-indigo-200 hover:bg-indigo-500 hover:text-white hover:border-indigo-500"
                  }`}
              >
                +{hidden.length}
              </div>

              {/* Floating popup */}
              {open && rect && (
                <div
                  onMouseEnter={cancelHide}
                  onMouseLeave={scheduleHide}
                  className="fixed z-[9999] bg-white border border-slate-200 rounded-xl overflow-hidden
                    shadow-[0_8px_28px_rgba(99,102,241,0.18),0_2px_8px_rgba(0,0,0,0.1)] min-w-[175px]"
                  style={{ left: rect.left - 20, top: rect.bottom + 5 }}
                >
                  <div className="px-3 py-1.5 bg-indigo-50 border-b border-indigo-100 flex items-center gap-1.5">
                    <span className="text-[10px] font-bold text-indigo-500 uppercase tracking-wide">
                      {hidden.length} more person{hidden.length > 1 ? "s" : ""}
                    </span>
                  </div>
                  <div className="p-2 flex flex-col gap-1.5">
                    {hidden.map((name, ni) => (
                      <RaciPersonCell
                        key={ni}
                        name={name}
                        personId={hiddenIds[ni]}
                        idx={ni + showCount}
                        size={22}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ══ SORT ICON ════════════════════════════════════════════════ */
const SortIcon = ({ active, order }) => (
  <span className="inline-flex flex-col gap-[1px] ml-1 shrink-0">
    <svg width="7" height="4" viewBox="0 0 7 4" className="block">
      <path d="M3.5 0L7 4H0L3.5 0Z" fill={active && order === "asc" ? "#6366F1" : "#CBD5E1"} />
    </svg>
    <svg width="7" height="4" viewBox="0 0 7 4" className="block">
      <path d="M3.5 4L0 0H7L3.5 4Z" fill={active && order === "desc" ? "#6366F1" : "#CBD5E1"} />
    </svg>
  </span>
);

/* ══ TASK NAME CELL — full-name tooltip pinned above the row,
   kept clear of the RACI card which always opens below ══════════ */
function TaskNameCell({ v, row, onTaskHover, onTaskLeave, onTaskClick }) {
  const [tipRect, setTipRect] = useState(null);
  const wrapRef  = useRef(null);
  const tipTimer = useRef(null);

  const handleEnter = (e) => {
    onTaskHover(row, e);
    clearTimeout(tipTimer.current);
    tipTimer.current = setTimeout(() => {
      if (wrapRef.current) setTipRect(wrapRef.current.getBoundingClientRect());
    }, 400);
  };
  const handleLeave = () => {
    onTaskLeave();
    clearTimeout(tipTimer.current);
    setTipRect(null);
  };

  return (
    <div ref={wrapRef} className="flex-1 min-w-0" onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
      <Text
        className="!text-[13px] !font-medium cursor-pointer !text-blue-700 min-w-0 break-all line-clamp-2 overflow-hidden !leading-[1.4]"
        onClick={() => onTaskClick(row._id)}
      >
        {v}
      </Text>

      {/* Full-name tooltip — anchored above the cell, RACI card always sits below it */}
      {tipRect && (
        <div
          className="fixed z-[2147483647] bg-slate-800 text-white text-[11.5px] font-medium leading-snug rounded-lg px-3 py-2 shadow-[0_8px_24px_rgba(0,0,0,0.28)] max-w-[340px] break-words pointer-events-none"
          style={{ left: Math.max(8, tipRect.left), top: Math.max(8, tipRect.top - 8), transform: "translateY(-100%)" }}
        >
          {v}
        </div>
      )}
    </div>
  );
}

/* ══ COLUMNS ══════════════════════════════════════════════════ */
const ALL_COLS = [
  "TASK ID", "TASK", "PRIORITY", "STATUS", "WORKSPACE",
  "FUNCTIONAL CATEGORIES", "ACCOUNTABLE", "RESPONSIBLE",
  "CONSULTED", "INFORMED", "CREATED AT", "START DATE",
  "COMPLETED AT", "COMPLETED BY", "DEADLINE",
];

const buildColumns = (
  onTaskHover, onTaskLeave, onTaskClick, onCommentClick,
  onCategoryEdit, onSubtaskToggle, expandedRows, loadingRows,
  selectedIds, onSelectOne, onSelectAll, hiddenCols, onVisibilityOpen,
  serverSortConfig, onServerSort, currentUserId, productOwnerWorkspaceCodes
) => [

  /* ── Checkbox ── */
  {
    title: "__CHECKBOX_HEADER__",
    key: "__check",
    dataIndex: "_id",
    width: 40,
    fixed: "left",
    render: (id, row) => {
      if (row.__isSubtask || row.__isSubtaskLoading || row.__isSubtaskEmpty) return null;
      const status = (row.task_status || "").toLowerCase();
      if (["completed", "complete", "closed"].includes(status)) return null;
      if (currentUserId) {
        const isResponsible = !!row.is_responsible;
        const isAccountable = !!row.is_accountable;
        const isProductOwner = Array.isArray(productOwnerWorkspaceCodes) &&
          productOwnerWorkspaceCodes.includes(String(row.product_code || ""));
        if (!isResponsible && !isAccountable && !isProductOwner) return null;
      }
      return (
        <input
          type="checkbox"
          className="w-3.5 h-3.5 cursor-pointer accent-indigo-500"
          checked={selectedIds.has(id)}
          onChange={e => onSelectOne(id, e.target.checked)}
          onClick={e => e.stopPropagation()}
        />
      );
    },
  },

  /* ── Task ID ── */
  {
    title: "TASK ID",
    dataIndex: "unique_seq_id",
    key: "unique_seq_id",
    width: 110,
    render: (v, row) => {
      if (row.__isSubtask) return (
        <div className="flex items-center gap-1 pl-2.5">
          <code className="text-[11px] text-indigo-700 bg-indigo-50 border-0 rounded-md px-1.5 py-0.5 font-mono">
            #{v}
          </code>
        </div>
      );
      if (row.__isSubtaskLoading || row.__isSubtaskEmpty) return null;

      const hasSubtasks = row.has_subtasks === true;
      const count       = row.subtask_count || 0;
      const isExpanded  = !!(expandedRows && expandedRows[row._id]);
      const isLoading   = !!(loadingRows && loadingRows[row._id]);

      return (
        <div className="inline-flex items-center gap-1.5 whitespace-nowrap">
          <code className="text-[11px] text-indigo-700 bg-indigo-50 border-0 rounded-md px-1.5 py-0.5 font-mono">
            #{v}
          </code>
          {hasSubtasks && (
            <Tooltip title={`${count} subtask${count !== 1 ? "s" : ""} — click to ${isExpanded ? "collapse" : "expand"}`}>
              <span
                className={`subtask-badge${isExpanded ? " expanded" : ""}${isLoading ? " loading" : ""}`}
                onClick={e => { e.stopPropagation(); !isLoading && onSubtaskToggle && onSubtaskToggle(row._id); }}
              >
                {isLoading
                  ? <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"
                      className="animate-[spin_0.7s_linear_infinite]">
                      <path d="M12 2a10 10 0 0 1 10 10" />
                    </svg>
                  : count}
              </span>
            </Tooltip>
          )}
        </div>
      );
    },
  },

  /* ── Task Name — SYS icon + name + comment ── */
  {
    title: "TASK",
    dataIndex: "task_type",
    key: "task_type",
    width: 290,
    fixed: "left",
    render: (v, row) => {
      if (row.__isSubtaskLoading) return (
        <div className="flex items-center gap-2 py-2 pl-5 text-slate-400 text-xs">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6366F1" strokeWidth="3"
            className="shrink-0 animate-[spin_0.7s_linear_infinite]">
            <path d="M12 2a10 10 0 0 1 10 10" />
          </svg>
          Loading subtasks...
        </div>
      );
      if (row.__isSubtaskEmpty) return (
        <div className="flex items-center gap-1.5 py-2 pl-5 text-slate-300 text-xs">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" /><line x1="8" y1="12" x2="16" y2="12" />
          </svg>
          No subtasks found
        </div>
      );

      const isSystem      = row.is_system_generated || false;
      const isOccurrence  = !!row.repeat;
      const commentCount  = row.notes_count || (Array.isArray(row.notes) ? row.notes.length : 0);

      return (
        <div
          className={`flex items-start gap-[5px] min-w-[230px] max-w-[400px] ${row.__isSubtask ? "pl-5" : "pl-0"}`}
        >
          {/* CPU chip — system generated */}
          {isSystem && (
            <Tooltip title="System Generated Task" placement="top">
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-[5px] bg-blue-50 border border-blue-200 shrink-0 mt-0.5 cursor-default">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                  stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="7" y="7" width="10" height="10" rx="1" />
                  <path d="M9 7V4M12 7V4M15 7V4" />
                  <path d="M9 20v-3M12 20v-3M15 20v-3" />
                  <path d="M7 9H4M7 12H4M7 15H4" />
                  <path d="M20 9h-3M20 12h-3M20 15h-3" />
                </svg>
              </span>
            </Tooltip>
          )}

          {/* Occurrence icon — recurring task */}
          {isOccurrence && (
            <Tooltip title="Recurring Task" placement="top">
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-[5px] bg-blue-50 border border-blue-200 shrink-0 mt-0.5 cursor-default">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                  stroke="#2563EB" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 2l4 4-4 4" />
                  <path d="M3 11V9a4 4 0 0 1 4-4h14" />
                  <path d="M7 22l-4-4 4-4" />
                  <path d="M21 13v2a4 4 0 0 1-4 4H3" />
                </svg>
              </span>
            </Tooltip>
          )}

          {/* Task name */}
          <TaskNameCell v={v} row={row} onTaskHover={onTaskHover} onTaskLeave={onTaskLeave} onTaskClick={onTaskClick} />

          {/* Comment icon + badge — single pill */}
          <Tooltip title="Add / View Comments" placement="top">
            <div
              onClick={(e) => { e.stopPropagation(); onCommentClick(row); }}
              className="shrink-0 cursor-pointer inline-flex items-center gap-[3px] mt-0.5 pl-1 pr-1.5 py-[3px] rounded-full border border-blue-100 bg-blue-50 transition-colors duration-150 hover:bg-blue-100 hover:border-blue-300"
            >
              <CommentIcon />
              <span className="text-[10px] font-bold text-blue-600 leading-none">
                {commentCount}
              </span>
            </div>
          </Tooltip>
        </div>
      );
    },
  },

  /* ── Priority ── */
  {
    title: "PRIORITY",
    dataIndex: "priority",
    key: "priority",
    width: 105,
    render: (v) => {
      const pc = priorityCfg[v] || priorityCfg["MEDIUM"];
      return <Tag color={pc.color} className="!text-[11px] !rounded-md">{v}</Tag>;
    },
  },

  /* ── Status ── */
  {
    title: "STATUS",
    dataIndex: "task_status",
    key: "task_status",
    width: 130,
    render: (v) => {
      const STATUS_MAP = {
        "open": "open", "in progress": "in-progress", "in-progress": "in-progress",
        "on hold": "on-hold", "on-hold": "on-hold", "completed": "complete",
        "complete": "complete", "approval pending": "approval pending", "closed": "closed",
      };
      const key = STATUS_MAP[(v || "").toLowerCase()] || "open";
      const sc  = statusCfg[key] || statusCfg["open"];
      return <Tag color={sc.color} className="!text-[11px] !rounded-md">{sc.label}</Tag>;
    },
  },

  /* ── Workspace ── */
  {
    title: "WORKSPACE",
    dataIndex: "product_name",
    key: "product_name",
    width: 150,
    render: (v) => (
      <Tag className="!text-[11px] !bg-slate-100 !text-slate-600 !border-none !rounded-md">
        {v || "—"}
      </Tag>
    ),
  },

  /* ── Functional Categories — first badge + "+N more" tooltip + edit icon ── */
  {
    title: "FUNCTIONAL CATEGORIES",
    dataIndex: "functional_categories",
    key: "functional_categories",
    width: 215,
    render: (v, row) => {
      const cats  = Array.isArray(v) ? v : (v ? [v] : []);
      const extra = cats.slice(1);
      return (
        <div className="flex items-center gap-1.5">
          <div className="flex items-center gap-[4px] flex-1 min-w-0">
            {cats.length > 0 ? (
              <>
                <Tag className={`!text-[11px] !rounded-md !m-0 !bg-blue-50 !text-blue-700 !border !border-blue-200 ${extra.length > 0 ? "!truncate !max-w-[140px]" : ""}`}>
                  {cats[0]}
                </Tag>
                {extra.length > 0 && (
                  <Tooltip
                    placement="top"
                    title={
                      <div className="flex flex-col gap-[5px] py-0.5">
                        {cats.map((c, i) => (
                          <span key={i} className="inline-block text-[11px] bg-blue-50 text-blue-700 border border-blue-200 rounded-md px-2 py-0.5 leading-4">
                            {c}
                          </span>
                        ))}
                      </div>
                    }
                    color="#fff"
                    overlayInnerStyle={{ padding: "8px 10px", borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.12)" }}
                  >
                    <span className="inline-flex items-center justify-center w-[22px] h-[22px] rounded-full text-[10px] font-bold bg-violet-50 text-violet-600 border border-violet-200 cursor-default select-none shrink-0 hover:bg-violet-500 hover:text-white hover:border-violet-500 transition-all duration-150">
                      +{extra.length}
                    </span>
                  </Tooltip>
                )}
              </>
            ) : (
              <Text type="secondary" className="!text-xs">—</Text>
            )}
          </div>

          {/* Edit icon */}
          <Tooltip title="Edit Categories" placement="top">
            <div
              onClick={(e) => { e.stopPropagation(); onCategoryEdit(row); }}
              className="group cat-plus-btn w-5 h-5 rounded-full bg-blue-50 border border-blue-200 flex items-center justify-center cursor-pointer shrink-0 transition-all duration-150 hover:bg-blue-600 hover:border-blue-600"
            >
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="2.5" strokeLinecap="round"
                className="cat-plus-svg pointer-events-none text-blue-600 group-hover:text-white transition-colors duration-150">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </div>
          </Tooltip>
        </div>
      );
    },
  },

  /* ── Accountable ── */
  {
    title: "ACCOUNTABLE",
    dataIndex: "task_created_by",
    key: "task_created_by",
    width: 170,
    render: (v, row) => {
      if (!v) return <Text type="secondary" className="!text-xs">—</Text>;
      return <RaciPersonCell name={v} personId={row.task_created_by_id} idx={0} size={24} />;
    },
  },

  /* ── Responsible — MoreNamesPill: shows 1, rest in "+N more" hover popup ── */
  {
    title: "RESPONSIBLE",
    dataIndex: "assigned_to_name",
    key: "assigned_to_name",
    width: 180,
    render: (v, row) => {
      const names = Array.isArray(v) ? v : (v ? [v] : []);
      const ids   = Array.isArray(row.assigned_to_ids) ? row.assigned_to_ids : [];
      return <MoreNamesPill names={names} ids={ids} size={22} showCount={1} />;
    },
  },

  /* ── Consulted — MoreNamesPill ── */
  {
    title: "CONSULTED",
    dataIndex: "observer_to_name",
    key: "observer_to_name",
    width: 170,
    render: (arr, row) => {
      const names = Array.isArray(arr) ? arr : (arr ? [arr] : []);
      const ids   = Array.isArray(row.observer_ids) ? row.observer_ids : [];
      return <MoreNamesPill names={names} ids={ids} size={22} showCount={1} />;
    },
  },

  /* ── Informed — MoreNamesPill ── */
  {
    title: "INFORMED",
    dataIndex: "informer_to_name",
    key: "informer_to_name",
    width: 170,
    render: (arr, row) => {
      const names = Array.isArray(arr) ? arr : (arr ? [arr] : []);
      const ids   = Array.isArray(row.informer_ids) ? row.informer_ids : [];
      return <MoreNamesPill names={names} ids={ids} size={22} showCount={1} />;
    },
  },

  /* ── Created At ── */
  {
    title: () => (
      <div className="flex items-center gap-0.5 cursor-pointer select-none" onClick={() => onServerSort(SORT_COL["task_created_at"])}>
        <span>CREATED AT</span>
        <SortIcon active={serverSortConfig.col === SORT_COL["task_created_at"]} order={serverSortConfig.dir} />
      </div>
    ),
    dataIndex: "task_created_at",
    key: "task_created_at",
    width: 165,
    render: (v) => <DateCell v={v} />,
  },

  /* ── Start Date ── */
  {
    title: () => (
      <div className="flex items-center gap-0.5 cursor-pointer select-none" onClick={() => onServerSort(SORT_COL["start_date"])}>
        <span>START DATE</span>
        <SortIcon active={serverSortConfig.col === SORT_COL["start_date"]} order={serverSortConfig.dir} />
      </div>
    ),
    dataIndex: "start_date",
    key: "start_date",
    width: 155,
    render: (v) => <DateCell v={v} />,
  },

  /* ── Completed At ── */
  {
    title: () => (
      <div className="flex items-center gap-0.5 cursor-pointer select-none" onClick={() => onServerSort(SORT_COL["completed_at"])}>
        <span>COMPLETED AT</span>
        <SortIcon active={serverSortConfig.col === SORT_COL["completed_at"]} order={serverSortConfig.dir} />
      </div>
    ),
    dataIndex: "completed_at",
    key: "completed_at",
    width: 165,
    render: (v) => <DateCellLocal v={v} />,
  },

  /* ── Completed By ── */
  {
    title: "COMPLETED BY",
    dataIndex: "completed_by",
    key: "completed_by",
    width: 150,
    render: (v, row) => {
      if (!v) return <Text type="secondary" className="!text-xs">—</Text>;
      return (
        <div className="flex items-center gap-1.5">
          <PhotoAvatar name={v} userId={row.completed_by_id} size={22} />
          <span className="text-[12px] text-[#374151] whitespace-nowrap overflow-hidden text-ellipsis max-w-[100px]">
            {v.split(" ")[0]}
          </span>
        </div>
      );
    },
  },

  /* ── Deadline — fixed right, column visibility trigger ── */
  {
    title: () => (
      <div className="flex items-center justify-between gap-1.5">
        <div className="flex items-center gap-0.5 cursor-pointer select-none" onClick={() => onServerSort(SORT_COL["completion_sla"])}>
          <span>DEADLINE</span>
          <SortIcon active={serverSortConfig.col === SORT_COL["completion_sla"]} order={serverSortConfig.dir} />
        </div>
        <span
          onClick={e => { e.stopPropagation(); onVisibilityOpen && onVisibilityOpen(e); }}
          title="Column visibility"
          className="inline-flex items-center justify-center w-[18px] h-[18px] rounded cursor-pointer bg-slate-100 text-slate-500 shrink-0 hover:bg-slate-200 transition-colors duration-150"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
          </svg>
        </span>
      </div>
    ),
    dataIndex: "completion_sla",
    key: "completion_sla",
    width: 170,
    fixed: "right",
    render: (v, row) => {
      if (!v) return <span className="text-xs text-gray-400">--</span>;
      const STATUS_MAP = {
        "open": "open", "in progress": "in-progress", "in-progress": "in-progress",
        "on hold": "on-hold", "on-hold": "on-hold", "completed": "complete",
        "complete": "complete", "approval pending": "approval pending", "closed": "closed",
      };
      const status   = STATUS_MAP[(row.task_status || "").toLowerCase()] || "open";
      const isFinal  = ["complete", "closed", "approval pending"].includes(status);
      const overdue  = new Date(v) < new Date() && !isFinal;

      // Deadline badge colors — reuses statusCfg for Complete/Closed/Approval Pending/Open,
      // with dedicated dark-green/yellow for In Progress/On Hold. Overdue still overrides to red.
      const DEADLINE_COLORS = {
        "in-progress": { bg: "#D1FAE5", color: "#065F46", border: "#6EE7B7" },
        "on-hold":     { bg: "#FEFCE8", color: "#A16207", border: "#FDE68A" },
      };
      const s = overdue
        ? { bg: "#FEF2F2", color: "#DC2626", border: "#FECACA" }
        : (DEADLINE_COLORS[status] || statusCfg[status] || statusCfg["open"]);
      return (
        <span
          className="inline-block text-[11px] font-semibold px-2.5 py-1 rounded-md whitespace-nowrap border"
          style={{ background: s.bg, color: s.color, borderColor: s.border }}
        >
          {fmtDate(v) || v}
        </span>
      );
    },
  },
];

/* ══ MAIN COMPONENT ═══════════════════════════════════════════ */
// Backend column indices for sortable date columns
const SORT_COL = {
  task_created_at: 10,
  start_date:      11,
  completed_at:    12,
  completion_sla:  14,
};

export default function ListView({ tasks = [], loading = false, onCommentClick, onBulkReady, onRefresh, serverSortConfig = { col: 10, dir: "desc" }, onServerSort, currentUserId = null, productOwnerWorkspaceCodes = [], selectionResetKey = 0 }) {
  const [raciRow,      setRaciRow]      = useState(null);
  const [raciPos,      setRaciPos]      = useState({ x: 0, y: 0 });
  const [detailTaskId, setDetailTaskId] = useState(null);
  const [commentTask,  setCommentTask]  = useState(null);
  const [catEditRow,   setCatEditRow]   = useState(null);

  const headerCheckRef = useRef();

  /* ── Checkbox + bulk ── */
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkConfirm, setBulkConfirm] = useState(false);

  /* ── Notify parent of selection ── */
  useEffect(() => {
    if (onBulkReady) onBulkReady(selectedIds);
  }, [selectedIds]);

  /* ── Parent bumps this after its own bulk action succeeds — the parent's
     "Mark Complete" only clears its own mirrored copy of the selection, so
     without this our internal selectedIds would keep the just-completed
     tasks' ids and silently fold them into the next selection. ── */
  useEffect(() => {
    if (selectionResetKey > 0) setSelectedIds(new Set());
  }, [selectionResetKey]); // eslint-disable-line

  /* ── Sync header checkbox indeterminate state ── */
  useEffect(() => {
    if (!headerCheckRef.current) return;
    const incompleteTasks = tasks.filter(t =>
      !["completed", "complete", "closed"].includes((t.task_status || "").toLowerCase())
    );
    const total = incompleteTasks.length;
    const sel   = selectedIds.size;
    headerCheckRef.current.checked       = sel > 0 && sel === total;
    headerCheckRef.current.indeterminate = sel > 0 && sel < total;
  }, [selectedIds, tasks]);

  /* ── Sort handled server-side via serverSortConfig + onServerSort props ── */

  /* ── Column visibility ── */
  const [hiddenCols, setHiddenCols] = useState(new Set());
  const [visOpen,    setVisOpen]    = useState(false);
  const [visPos,     setVisPos]     = useState({ top:0, right:0 });
  const visRef = useRef();

  useEffect(() => {
    if (!visOpen) return;
    const fn = e => { if (visRef.current && !visRef.current.contains(e.target)) setVisOpen(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, [visOpen]);

  const handleSelectOne = useCallback((id, checked) => {
    setSelectedIds(prev => { const n = new Set(prev); checked ? n.add(id) : n.delete(id); return n; });
  }, []);

  const handleSelectAll = useCallback((checked) => {
    if (checked) {
      const incomplete = tasks
        .filter(t => !["completed", "complete", "closed"].includes((t.task_status || "").toLowerCase()))
        .map(t => t._id);
      setSelectedIds(new Set(incomplete));
    } else {
      setSelectedIds(new Set());
    }
  }, [tasks]);

  const handleBulkComplete = async () => {
    if (selectedIds.size === 0) return;
    setBulkLoading(true);
    try {
      await axiosInstance.post("/task_assignment/bulk_complete", { task_ids: Array.from(selectedIds) });
      setSelectedIds(new Set());
      setBulkConfirm(false);
    } catch (e) {
      console.error("Bulk complete failed", e);
    } finally {
      setBulkLoading(false);
    }
  };

  const toggleColVisibility = (colTitle) => {
    setHiddenCols(prev => { const n = new Set(prev); n.has(colTitle) ? n.delete(colTitle) : n.add(colTitle); return n; });
  };

  /* ── Subtask expand/collapse ── */
  const [expandedRows, setExpandedRows] = useState({});
  const [loadingRows,  setLoadingRows]  = useState({});

  const toggleSubtasks = useCallback(async (taskId) => {
    if (expandedRows[taskId]) {
      setExpandedRows(prev => { const n = { ...prev }; delete n[taskId]; return n; });
      return;
    }
    setLoadingRows(prev => ({ ...prev, [taskId]: true }));
    try {
      const res = await axiosInstance.get(`/task_assignment/get_task_hierarchy/${taskId}`);
      const raw = res.data?.subtasks || [];
      const subtasks = raw.map(st => ({
        ...st,
        task_created_by:    st.task_created_by    || st.task_created_by_name || st.created_by_name || "",
        task_created_by_id: st.task_created_by_id || st.created_by_id        || "",
        assigned_to_name:   Array.isArray(st.assigned_to_name) ? st.assigned_to_name
                              : st.assigned_to_name  ? [st.assigned_to_name]
                              : st.responsible_name  ? [st.responsible_name] : [],
        assigned_to_ids:    Array.isArray(st.assigned_to_ids)  ? st.assigned_to_ids
                              : st.assigned_to_id    ? [st.assigned_to_id]  : [],
        observer_to_name:   Array.isArray(st.observer_to_name) ? st.observer_to_name
                              : st.observer_to_name  ? [st.observer_to_name] : [],
        observer_ids:       Array.isArray(st.observer_ids)     ? st.observer_ids     : [],
        informer_to_name:   Array.isArray(st.informer_to_name) ? st.informer_to_name
                              : st.informer_to_name  ? [st.informer_to_name] : [],
        informer_ids:       Array.isArray(st.informer_ids)     ? st.informer_ids     : [],
      }));
      setExpandedRows(prev => ({ ...prev, [taskId]: subtasks }));
    } catch {
      setExpandedRows(prev => ({ ...prev, [taskId]: [] }));
    } finally {
      setLoadingRows(prev => { const n = { ...prev }; delete n[taskId]; return n; });
    }
  }, [expandedRows]);

  const showTimer = useRef(null);
  const hideTimer = useRef(null);
  const overModal = useRef(false);
  const HOVER_OPEN_DELAY = 500;

  /* ── RACI hover ── */
  const handleTaskHover = useCallback((row, e) => {
    clearTimeout(hideTimer.current);
    const rect = e.currentTarget.getBoundingClientRect();
    showTimer.current = setTimeout(() => {
      setRaciPos({ x: rect.left, y: rect.bottom });
      setRaciRow(row);
    }, HOVER_OPEN_DELAY);
  }, []);

  const handleTaskLeave = useCallback(() => {
    clearTimeout(showTimer.current);
    hideTimer.current = setTimeout(() => { if (!overModal.current) setRaciRow(null); }, 200);
  }, []);

  const handleModalEnter = useCallback(() => {
    overModal.current = true;
    clearTimeout(hideTimer.current);
  }, []);

  const handleModalLeave = useCallback(() => {
    overModal.current = false;
    hideTimer.current = setTimeout(() => setRaciRow(null), 200);
  }, []);

  /* ── Task detail ── */
  const handleTaskClick = useCallback((taskId) => {
    setDetailTaskId(taskId);
  }, []);

  /* ── Comment ── */
  const handleCommentClick = useCallback((row) => {
    const taskObj = { _id: row._id, name: row.task_type };
    if (typeof onCommentClick === "function") {
      onCommentClick(taskObj);
    } else {
      setCommentTask(taskObj);
    }
  }, [onCommentClick]);

  /* ── Category edit ── */
  const handleCategoryEdit = useCallback((row) => {
    setCatEditRow(row);
  }, []);

  /* ── Build merged dataSource — inject subtask rows after parent ── */
  const activeSubtaskIds = new Set(
    Object.values(expandedRows).flatMap(subs => subs.map(s => s._id))
  );

  const mergedTasks = [];
  tasks.forEach(task => {
    if (activeSubtaskIds.has(task._id)) return;
    mergedTasks.push(task);
    if (expandedRows[task._id]) {
      const subtasks = expandedRows[task._id];
      if (subtasks.length === 0) {
        mergedTasks.push({ _id: `${task._id}_empty`, __isSubtaskEmpty: true, __parentId: task._id });
      } else {
        subtasks.forEach(st => mergedTasks.push({ ...st, __isSubtask: true, __parentId: task._id }));
      }
    }
    if (loadingRows[task._id]) {
      mergedTasks.push({ _id: `${task._id}_loading`, __isSubtaskLoading: true, __parentId: task._id });
    }
  });

  /* ── Filter hidden columns + fix checkbox header ── */
  const visibleColumns = buildColumns(
    handleTaskHover, handleTaskLeave, handleTaskClick,
    handleCommentClick, handleCategoryEdit,
    toggleSubtasks, expandedRows, loadingRows,
    selectedIds, handleSelectOne, handleSelectAll,
    hiddenCols, (e) => {
      e.stopPropagation();
      const r = e.currentTarget.getBoundingClientRect();
      setVisPos({ top: r.bottom + 6, right: window.innerWidth - r.right });
      setVisOpen(p => !p);
    },
    serverSortConfig, onServerSort, currentUserId, productOwnerWorkspaceCodes
  )
    .filter(col => {
      const t = typeof col.title === "string" ? col.title : col.key;
      return !hiddenCols.has(t);
    })
    .map(col => {
      if (col.key === "__check") {
        return {
          ...col,
          title: () => (
            <input
              ref={headerCheckRef}
              type="checkbox"
              className="w-3.5 h-3.5 cursor-pointer accent-indigo-500"
              onChange={e => handleSelectAll(e.target.checked)}
            />
          ),
        };
      }
      return col;
    });

  return (
    <>
      {/* ── Table wrapper ── */}
      <div className="relative m-5 rounded-xl overflow-hidden shadow-[0_1px_4px_rgba(0,0,0,0.08)] border border-[#E8EAF0]">

        {/* Column visibility popup */}
        {visOpen && (
          <div
            ref={visRef}
            className="fixed z-[9999] bg-white border border-slate-200 rounded-[10px] shadow-[0_8px_24px_rgba(0,0,0,0.12)] w-[220px] overflow-hidden"
            style={{ top: visPos.top, right: visPos.right }}
          >
            <div className="px-3.5 py-2.5 border-b border-slate-100 text-[11px] font-bold text-slate-500 uppercase tracking-[0.5px]">
              Column Visibility
            </div>
            <div className="max-h-[320px] overflow-y-auto">
              {ALL_COLS.map(col => (
                <div
                  key={col}
                  onClick={() => toggleColVisibility(col)}
                  className={`flex items-center justify-between px-3.5 py-2 cursor-pointer border-b border-slate-50 transition-colors duration-150 hover:bg-indigo-50 ${
                    hiddenCols.has(col) ? "bg-white" : "bg-slate-50"
                  }`}
                >
                  <span className={`text-xs font-medium ${hiddenCols.has(col) ? "text-gray-400" : "text-gray-700"}`}>
                    {col}
                  </span>
                  {/* Toggle track */}
                  <div className={`w-8 h-[18px] rounded-[20px] relative transition-colors duration-150 ${
                    hiddenCols.has(col) ? "bg-slate-200" : "bg-indigo-500"
                  }`}>
                    {/* Toggle thumb — left-0.5 = 2px, left-3.5 = 14px in Tailwind */}
                    <div className={`absolute top-0.5 w-3.5 h-3.5 rounded-full bg-white shadow-sm transition-all duration-150 ${
                      hiddenCols.has(col) ? "left-0.5" : "left-3.5"
                    }`} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <Table
          rowKey={(r) => r._id || r.id}
          columns={visibleColumns}
          dataSource={mergedTasks}
          loading={loading}
          scroll={{ x: "max-content" }}
          size="small"
          pagination={false}
          rowClassName={(row, i) => {
            if (row.__isSubtask)        return "subtask-row-react";
            if (row.__isSubtaskLoading) return "subtask-row-react subtask-loading-row";
            if (row.__isSubtaskEmpty)   return "subtask-row-react subtask-empty-row";
            if (expandedRows[row._id])  return "parent-expanded-row";
            return i % 2 === 0 ? "task-row-even" : "task-row-odd";
          }}
          className="!text-[13px]"
        />

        {/* Ant Design overrides — must stay in <style> since these target AntD internal classes */}
        <style>{`
          .task-row-even td { background: #ffffff !important; }
          .task-row-odd  td { background: #FAFBFE !important; }
          .subtask-row-react td { background: #F8F9FF !important; }
          .subtask-row-react:hover td { background: #E8EEFF !important; }
          .parent-expanded-row td { background: #EEF2FF !important; }
          .ant-table-thead > tr > th {
            font-size: 10.5px !important;
            font-weight: 700 !important;
            color: #94A3B8 !important;
            letter-spacing: .08em !important;
            background: #F8FAFC !important;
          }
          .ant-table-pagination { display: none !important; }
          .cat-plus-btn:hover .cat-plus-svg { stroke: #fff !important; }
          .subtask-badge {
            width: 17px; height: 17px;
            border: 2px solid #6366F1;
            background: white; border-radius: 4px;
            display: inline-flex; align-items: center; justify-content: center;
            cursor: pointer; font-weight: 700; font-size: 10px;
            color: #6366F1; transition: all .2s; flex-shrink: 0;
          }
          .subtask-badge:hover { background: #6366F1; color: #fff; }
          .subtask-badge.expanded { background: #6366F1; color: #fff; }
          .subtask-badge.loading { opacity: .6; cursor: wait; }
          @keyframes spin { to { transform: rotate(360deg); } }
        `}</style>

        {/* RACI hover popup */}
        <ListViewRaciModal
          row={raciRow}
          pos={raciPos}
          onEnter={handleModalEnter}
          onLeave={handleModalLeave}
        />
      </div>

      {/* ── Task Detail Panel ── */}
      <TaskDetailPanel
        taskId={detailTaskId}
        onClose={() => setDetailTaskId(null)}
        onAction={onRefresh}
      />

      {/* ── Comment Panel — local fallback ── */}
      {!onCommentClick && (
        <CommentPanel
          task={commentTask}
          onClose={() => setCommentTask(null)}
          onCommented={onRefresh}
        />
      )}

      {/* ── Category Edit Popup ── */}
      {catEditRow && (
        <CategoryEditPopup
          taskId={catEditRow._id}
          currentCategories={
            Array.isArray(catEditRow.functional_categories)
              ? catEditRow.functional_categories
              : []
          }
          onClose={() => setCatEditRow(null)}
          onSaved={() => {
            setCatEditRow(null);
            if (onRefresh) onRefresh();
          }}
        />
      )}
    </>
  );
}